import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import clsx from "clsx";
import type { FunctionComponent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Table from "~/components/Table";
import { INVOICE_DETAILS_PATH_WITH_ORIGIN } from "~/constants/appRoutes.ts";
import { INVOICES_SHARED_FILTER_VALUES, INVOICES_UNPAID_PAGE_KEY } from "~/constants/pageStateStorageKeys.ts";
import { useAuth } from "~/contexts/AuthContext";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import {
	applyDeliveryPeriodFilter,
	castFilterDatesToDate,
} from "~/modules/billing/components/DeliveryPeriodFilter/deliveryPeriodFilterUtils.ts";
import {
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import InvoicePageHeader from "~/modules/billing/components/InvoicePageHeader";
import DownloadUnpaidInvoicesTableDataButton
	from "~/modules/billing/components/UnpaidInvoicesIndexView/components/DownloadUnpaidInvoicesTableDataButton";
import {
	unpaidInvoicesIndexTableColumnDefs,
} from "~/modules/billing/components/UnpaidInvoicesIndexView/unpaidInvoiceIndexTableColumnDefs.tsx";
import type {
	InvoiceIndexTableData,
} from "~/modules/billing/components/UnpaidInvoicesIndexView/unpaidInvoicesIndexTableTypes.ts";
import {
	InvoicePageTabId,
	invoiceTablesDefaultPageState,
	invoiceTableSharedFilterDefaultPageState,
} from "~/modules/billing/utils/billingUtils.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";

type Props = {
	isLoading: boolean;
	tableData: InvoiceIndexTableData[];
};

const UnpaidInvoicesIndexView: FunctionComponent<Props> = ({ isLoading, tableData }) => {
	const {hasAnyPermission} = useAuth();
	const userCanManageInvoices = hasAnyPermission([PermissionNames.CanManageInvoices]);
	const { pageState, setPageState } = usePageStateStorage({
		pageKey: INVOICES_UNPAID_PAGE_KEY,
		defaultState: invoiceTablesDefaultPageState,
	});

	const { pageState: sharedFilterPageState, setPageState: setSharedFilterPageState } = usePageStateStorage({
		pageKey: INVOICES_SHARED_FILTER_VALUES,
		defaultState: invoiceTableSharedFilterDefaultPageState,
	});
	const [deliveryPeriodFilter, setDeliveryPeriodFilter] = useState<DateRangeNullable>(castFilterDatesToDate(sharedFilterPageState.deliveryPeriodFilter));
	const [globalFilter, setGlobalFilter] = useState<string>(sharedFilterPageState.globalFilter);
	const [sorting, setSorting] = useState<SortingState>(pageState.sorting);

	useEffect(() => {
		setPageState({ sorting });
	}, [setPageState, sorting]);

	useEffect(() => {
		setSharedFilterPageState({ globalFilter, deliveryPeriodFilter });
	}, [deliveryPeriodFilter, globalFilter, setSharedFilterPageState]);

	const navigate = useNavigate();

	const preFilteredTableData = useMemo(() => {
		return applyDeliveryPeriodFilter(deliveryPeriodFilter, tableData);
	}, [tableData, deliveryPeriodFilter]);

	const handleRowClick = useCallback(
		(row: Row<InvoiceIndexTableData>, event: MouseEvent) => {
			const path = INVOICE_DETAILS_PATH_WITH_ORIGIN(row.original.id, validInvoiceDetailsNavigatedFromOrigins.UNPAID_INVOICES_INDEX);

			if (event.ctrlKey || event.metaKey) {
				window.open(path, "_blank");
			} else {
				navigate(path);
			}
		},
		[navigate],
	);

	const table = useReactTable({
		data: preFilteredTableData,
		columns: unpaidInvoicesIndexTableColumnDefs,
		onSortingChange: setSorting,
		state: {
			globalFilter,
			sorting,
		},
		meta: {
			onRowClick: handleRowClick,
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		sortDescFirst: true,
	});

	return <div className={clsx("auto-grid-2-rows mx-auto h-screen px-8")}>
		<InvoicePageHeader activeTabId={InvoicePageTabId.UNPAID}
						   deliveryPeriodFilter={deliveryPeriodFilter}
						   globalFilter={globalFilter}
						   setDeliveryPeriodFilter={setDeliveryPeriodFilter}
						   setGlobalFilter={setGlobalFilter}
						   userCanManageInvoices={userCanManageInvoices}
		>
			<DownloadUnpaidInvoicesTableDataButton deliveryPeriodFilter={deliveryPeriodFilter}
												   filteredTableData={table.getFilteredRowModel().rows.map(row => row.original)}
												   search={globalFilter} />
		</InvoicePageHeader>

		<div className="h-full overflow-hidden pb-10">
			<Table table={table}
				   isLoading={isLoading}
				   fixedWidth={true}
			/>
		</div>
	</div>;

};

export default UnpaidInvoicesIndexView;